import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Truck, User } from "lucide-react";

function hoursBetween(start, end) {
  if (!start || !end) return null;
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e)) return null;
  const h = (e - s) / 3600000;
  return h >= 0 ? Math.round(h * 100) / 100 : null;
}

export function ConsuntivoDialog({ open, onOpenChange, service, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && service) {
      const svcDates = service.dates?.length ? service.dates : [service.date_time];
      setRows(
        (service.signups || []).map((s) => {
          const chosen = s.dates && s.dates.length ? s.dates : svcDates;
          const d0 = chosen[0] || service.date_time || "";
          const endTime = service.end_times?.[d0];
          const endDefault = d0 && endTime ? `${d0.slice(0, 10)}T${endTime}` : "";
          return {
            user_id: s.user_id,
            name: s.name,
            slot: s.slot,
            actual_start: s.actual_start || d0 || "",
            actual_end: s.actual_end || endDefault,
            expected: hoursBetween(d0, endDefault),
          };
        })
      );
    }
  }, [open, service]);

  const setRow = (uid, k, v) => setRows((p) => p.map((r) => (r.user_id === uid ? { ...r, [k]: v } : r)));

  const submit = async () => {
    setLoading(true);
    try {
      const entries = rows
        .filter((r) => r.actual_start && r.actual_end)
        .map((r) => ({ user_id: r.user_id, actual_start: r.actual_start, actual_end: r.actual_end }));
      const { data } = await api.post(`/services/${service.id}/consuntivo`, { entries });
      toast.success("Consuntivo ore salvato");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Consuntivo ore · {service.event_name}</DialogTitle>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">Nessun volontario iscritto a questo servizio.</p>
        ) : (
          <div className="space-y-4" data-testid="consuntivo-form">
            <p className="text-xs text-muted-foreground">
              Le ore sono pre-compilate dagli orari del servizio. Modifica inizio e fine se il turno è durato di più o di meno.
            </p>
            {rows.map((r) => {
              const h = hoursBetween(r.actual_start, r.actual_end);
              const diff = h != null && r.expected != null ? Math.round((h - r.expected) * 100) / 100 : null;
              return (
                <div key={r.user_id} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className="font-semibold text-sm flex items-center gap-1.5">
                      {r.slot === "ambulanza" ? <Truck className="h-4 w-4" /> : <User className="h-4 w-4" />}
                      {r.name}
                    </span>
                    <div className="flex items-center gap-2">
                      {r.expected != null && (
                        <span className="text-[11px] text-muted-foreground" data-testid={`consuntivo-expected-${r.user_id}`}>
                          Previste: {r.expected} h
                        </span>
                      )}
                      {h != null && (
                        <Badge variant={diff ? "default" : "secondary"} data-testid={`consuntivo-hours-${r.user_id}`}>
                          {h} h{diff ? ` (${diff > 0 ? "+" : ""}${diff})` : ""}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Inizio</label>
                      <Input type="datetime-local" data-testid={`consuntivo-start-${r.user_id}`} value={r.actual_start} onChange={(e) => setRow(r.user_id, "actual_start", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Fine</label>
                      <Input type="datetime-local" data-testid={`consuntivo-end-${r.user_id}`} value={r.actual_end} onChange={(e) => setRow(r.user_id, "actual_end", e.target.value)} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={loading || rows.length === 0} data-testid="save-consuntivo-button" className="rounded-full font-semibold">
            {loading ? "Salvataggio…" : "Salva ore"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
